import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeSlash, SpinnerGap, CheckCircle, ArrowSquareOut } from "@phosphor-icons/react";
import api from "@/lib/api";
import { captureReferFromUrl, getReferCookie } from "@/shared/auth/referCookie";
import SiteFooter from "@/components/SiteFooter";
import SocialAuthButtons, { SocialAuthDivider } from "@/components/auth/SocialAuthButtons";
import { getPendingCart, claimPendingCart, getPostAuthTarget, clearPostAuthTarget } from "@/shared/auth/pendingCart";
import { myApi } from "@/my/api";
import { useRecaptchaExecute } from "@/shared/recaptcha";
import { emailSyntaxError } from "@/shared/emailValidation";
import { useEmailCheck, EmailHint } from "@/shared/useEmailCheck";

/**
 * Página pública de registo IHTP — clone visual 1:1 da landing
 * `darkcyan-turkey-901446.hostingersite.com/inicia-a-tua-experiencia`.
 *
 * Como replica pixel-perfect:
 *   1. O HTML completo do conteúdo Elementor foi extraído para
 *      `/register-clone/content.html` (com o widget do form original
 *      substituído por `<div id="ihtp-react-form-mount">`).
 *   2. Os stylesheets originais do Elementor + Astra + tema filho +
 *      Typekit (Nexa) são injectados dinamicamente no `<head>`.
 *   3. O nosso form React é montado no placeholder via `createPortal`
 *      — mantendo toda a lógica: reCAPTCHA v3 action "register",
 *      cookie REFER (URL + persistência), pendingCart claim pós-registo.
 */

// Cópia 100% local dos assets (CSS bundle + typekit + fontes + imagens).
// Descarregada da página live em Jul/2026 e servida a partir de
// `/app/frontend/public/register-clone/`. Já não dependemos de nenhum
// servidor externo — a página funciona mesmo que o WordPress em
// darkcyan-turkey-901446.hostingersite.com desapareça.
const STYLESHEETS = [
  "/register-clone/assets/css/bundle.css",
  "/register-clone/assets/css/typekit.css",
];

// Overrides que não conseguimos herdar do Elementor pois estão embutidos
// no `elementor-frontend.js` runtime (background slideshow) ou dependem de
// context runtime (flex-direction default = row). Injectamos como <style>
// interno com prioridade alta.
const RUNTIME_OVERRIDES_CSS = `
/* ══════════════════════════════════════════════════════════════════
 * OVERRIDES — pequenos ajustes por cima do bundle.css original.
 * Necessários porque o Elementor JS runtime (frontend.min.js) não é
 * carregado — logo alguns comportamentos JS (slideshow, carousel)
 * têm de ser resolvidos via CSS puro.
 * ══════════════════════════════════════════════════════════════ */

/* ── Hero: fixar o slideshow numa imagem única (JS não carregado) ── */
.elementor-3192 .elementor-element-153ba7f {
  background-image: url('/register-clone/assets/img/d9f0cb87491e93d61b2f8f342374cbbc2f3a5081-scaled.jpg') !important;
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
}

/* ── Benefits row: grid custom (substitui o Elementor Nested Carousel) ── */
.elementor-3192 .ihtp-benefits-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;
  max-width: 1200px;
  margin: 40px auto 0;
  padding: 0 20px;
  justify-items: center;
  box-sizing: border-box;
}
.elementor-3192 .ihtp-benefit-card {
  width: 100%;
  max-width: 360px;
  background: #0F1829;
  border-radius: 12px;
  padding: 32px 26px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-sizing: border-box;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0,0,0,0.25);
}
.elementor-3192 .ihtp-benefit-icon { height: 48px; display: flex; align-items: center; }
.elementor-3192 .ihtp-benefit-icon svg { max-height: 40px; width: auto; }
.elementor-3192 .ihtp-benefit-title {
  font-family: "the-seasons", "Cormorant Garamond", Georgia, serif !important;
  font-weight: 600;
  font-size: 36px;
  line-height: 1.15;
  color: #fff !important;
  margin: 0;
  min-height: 2.3em;
}
.elementor-3192 .ihtp-benefit-desc {
  font-family: "nexa", "Poppins", "Segoe UI", sans-serif !important;
  font-size: 15px;
  line-height: 1.55;
  color: rgba(255,255,255,0.85) !important;
  margin: 0;
}
.elementor-3192 .ihtp-benefit-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.elementor-3192 .ihtp-benefit-list li {
  font-family: "nexa", "Poppins", "Segoe UI", sans-serif !important;
  font-size: 15px; line-height: 1.5; color: rgba(255,255,255,0.85);
}
@media (min-width: 768px) {
  .elementor-3192 .ihtp-benefits-grid { grid-template-columns: repeat(4, 1fr); gap: 24px; }
  .elementor-3192 .ihtp-benefit-card { max-width: none; min-height: 440px; }
}

/* ── Icon list (fora dos benefícios) ── */
.elementor-3192 .elementor-icon-list-icon svg,
.elementor-3192 .elementor-icon-list-icon i {
  width: 14px !important; height: 14px !important; font-size: 14px !important; min-width: 14px;
}
.elementor-3192 .elementor-icon-list-icon { width: 20px !important; display: inline-flex; align-items: center; margin-right: 8px !important; }
.elementor-3192 .elementor-icon-list-item { display: flex !important; align-items: flex-start; margin-bottom: 8px !important; }

/* ── Swiper arrows: remover as setas gigantes do carousel de níveis ── */
.elementor-3192 .swiper-button-next,
.elementor-3192 .swiper-button-prev,
.elementor-3192 .elementor-swiper-button,
.elementor-3192 [class*="swiper-button"] { display: none !important; }

/* ── Botão "SABE MAIS SOBRE AS OPORTUNIDADES IHTP": ocultar ── */
.elementor-3192 .elementor-element-ff7613e { display: none !important; }

@media (max-width: 767px) {
  /* Ocultar níveis (grid desktop + carousel mobile) em mobile */
  .elementor-3192 .elementor-element-1787a12,
  .elementor-3192 .elementor-element-f279d14,
  .elementor-3192 .elementor-element-d41bb2d { display: none !important; }
}
`;

const today = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const PAISES = [
  "Portugal", "Brasil", "Angola", "Moçambique", "Cabo Verde",
  "Guiné-Bissau", "São Tomé e Príncipe", "Timor-Leste",
  "Espanha", "França", "Reino Unido", "Alemanha", "Suíça",
  "Luxemburgo", "Estados Unidos", "Canadá", "Outro",
];

// Injecta os stylesheets uma única vez. Se já existirem, ignora.
function useElementorStyles(active) {
  useEffect(() => {
    if (!active) return;
    const added = [];
    STYLESHEETS.forEach((href) => {
      if (document.querySelector(`link[data-ihtp-clone-css="${href}"]`)) return;
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = href;
      l.dataset.ihtpCloneCss = href;
      document.head.appendChild(l);
      added.push(l);
    });
    // Adiciona também o <style> com os overrides runtime (último, para
    // ter maior especificidade que o post-3192.css).
    let overrideEl = document.querySelector('style[data-ihtp-clone-overrides="1"]');
    if (!overrideEl) {
      overrideEl = document.createElement("style");
      overrideEl.dataset.ihtpCloneOverrides = "1";
      overrideEl.textContent = RUNTIME_OVERRIDES_CSS;
      document.head.appendChild(overrideEl);
      added.push(overrideEl);
    }
    return () => { added.forEach((l) => l.remove()); };
  }, [active]);
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [referId, setReferId] = useState(null);
  const execRecaptcha = useRecaptchaExecute();

  const [html, setHtml] = useState(null);
  const [htmlErr, setHtmlErr] = useState(false);
  const [mount, setMount] = useState(null); // portal target #ihtp-react-form-mount
  const containerRef = useRef(null);

  useElementorStyles(true);

  // 1) Carrega o HTML clonado (só state)
  useEffect(() => {
    let cancelled = false;
    fetch("/register-clone/content.html")
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
      .then((t) => { if (!cancelled) setHtml(t); })
      .catch(() => { if (!cancelled) setHtmlErr(true); });
    return () => { cancelled = true; };
  }, []);

  // 2) Depois de o HTML estar em state E o div estar no DOM, injecta
  // imperativamente (uma única vez) para não colidir com o portal.
  useEffect(() => {
    if (!html || !containerRef.current) return;
    if (containerRef.current.dataset.injected) return;
    containerRef.current.innerHTML = html;
    containerRef.current.dataset.injected = "1";
    // Localiza o placeholder do form React após reflow
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = containerRef.current?.querySelector("#ihtp-react-form-mount");
        if (el) {
          el.style.width = "100%";
          el.style.minHeight = "400px";
          el.style.display = "block";
          setMount(el);
        }
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [html]);

  const [form, setForm] = useState({
    email: "", nome_completo: "", password: "", confirm_password: "",
    data_nascimento: "", sexo: "", telemovel: "", email_recomendacao: "",
    pais: "", codigo_postal: "", localidade: "",
    aceito_termos: false,
  });
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState("");
  const [done, setDone] = useState(false);
  // Validação de email nível 2 (sintaxe + domínio real) — bloqueia o registo.
  const emailChk = useEmailCheck({ mode: "block" });

  useEffect(() => {
    const fromUrl = captureReferFromUrl();
    setReferId(fromUrl || getReferCookie());
  }, [searchParams]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateClient = () => {
    if (!form.nome_completo.trim()) return ["Nome completo obrigatório.", "nome_completo"];
    const emailErr = emailSyntaxError(form.email);
    if (emailErr) return [emailErr, "email"];
    if (form.password.length < 8) return ["A palavra-passe deve ter pelo menos 8 caracteres.", "password"];
    if (form.password !== form.confirm_password) return ["A confirmação da palavra-passe não coincide.", "confirm_password"];
    if (!/^\d{2}-\d{2}-\d{4}$/.test(form.data_nascimento)) return ["Data de nascimento no formato DD-MM-AAAA.", "data_nascimento"];
    if (!form.sexo) return ["Indique o sexo.", "sexo"];
    if (!form.telemovel.trim()) return ["Telemóvel obrigatório.", "telemovel"];
    if (!form.aceito_termos) return ["Tem de aceitar os termos e condições.", "aceito_termos"];
    return [null, null];
  };

  // Mostra o erro onde o cliente está a olhar: destaca o campo, faz scroll até
  // ele e coloca o cursor lá. O banner de erro aparece no topo e junto ao botão.
  const falhar = (msg, campo) => {
    setError(msg);
    setErrorField(campo || "");
    setTimeout(() => {
      const alvo = campo ? document.getElementById(`reg-${campo}`) : null;
      const el = alvo || document.querySelector('[data-testid="register-error-inline"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (alvo && typeof alvo.focus === "function") alvo.focus({ preventScroll: true });
      }
    }, 60);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorField("");
    const [err, campo] = validateClient();
    if (err) { falhar(err, campo); return; }
    // Confirma no servidor que o domínio do email existe e recebe email.
    const ev = await emailChk.check(form.email);
    if (ev.blocked) {
      falhar(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message, "email");
      return;
    }
    setLoading(true);
    try {
      const recaptchaToken = await execRecaptcha("register");
      const { data } = await api.post("/auth/register", { ...form, recaptcha_token: recaptchaToken });
      if (!data?.success) { falhar(data?.detail || "Erro ao criar conta."); return; }
      setDone(true);
      const hasPending = !!getPendingCart();
      if (hasPending) {
        try { await claimPendingCart(myApi); } catch (_) { /* ignore */ }
        const target = getPostAuthTarget() || "/my/carrinho";
        clearPostAuthTarget();
        setTimeout(() => { window.location.href = target; }, 1200);
        return;
      }
      setTimeout(() => { window.location.href = data.redirect || "/my"; }, 1800);
    } catch (e2) {
      falhar(e2?.response?.data?.detail || "Erro ao criar conta. Tente novamente.");
    } finally { setLoading(false); }
  };

  const formStyle = useMemo(() => ({
    // Herda a fonte Nexa carregada pelo Typekit. As cores estão no <style>
    // interno abaixo — o form vive numa secção Elementor com fundo bege
    // (#D1CFC9), portanto usa texto dark #0F1829 para legibilidade.
    fontFamily: "'nexa', 'Poppins', 'Segoe UI', sans-serif",
  }), []);

  // ── Estados finais ─────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen bg-[#0F1829] flex items-center justify-center p-6 text-white">
        <div className="text-center max-w-md w-full">
          <CheckCircle size={64} weight="fill" className="mx-auto mb-5 text-emerald-400" />
          <h2 className="text-3xl font-semibold mb-2">Conta criada com sucesso!</h2>
          <p className="text-sm text-white/70">A redireccionar para o portal I Have the Power…</p>
          <SpinnerGap size={22} className="animate-spin mx-auto mt-4 text-white/80" />
        </div>
      </div>
    );
  }

  if (htmlErr) {
    return (
      <div className="min-h-screen bg-[#0F1829] text-white flex items-center justify-center p-6" data-testid="register-clone-error">
        <div className="text-center max-w-md">
          <p className="mb-3">Não foi possível carregar a página de registo.</p>
          <Link to="/login" className="underline text-white/80">Voltar ao login</Link>
        </div>
      </div>
    );
  }

  if (!html) {
    return (
      <div className="min-h-screen bg-[#0F1829] flex items-center justify-center text-white" data-testid="register-loading">
        <SpinnerGap size={40} className="animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* HTML clonado do Elementor — injectado imperativamente uma única
          vez (via containerRef acima) para não colidir com o portal do form. */}
      <div ref={containerRef} data-testid="register-landing" />

      {/* Portal: monta o nosso form React dentro do placeholder Elementor */}
      {mount && createPortal(
        <div style={formStyle} data-testid="register-form-wrap"
          className="ihtp-form-block">
          <style>{`
            .ihtp-form-block { width: 100%; max-width: 640px; margin: 0 auto; color: #0F1829; }
            .ihtp-form-block .fld { margin-bottom: 18px; }
            .ihtp-form-block label.lbl {
              display: block; font-size: 11px; font-weight: 600;
              letter-spacing: 0.2em; text-transform: uppercase;
              color: #0F1829; margin-bottom: 8px; opacity: 0.85;
            }
            .ihtp-form-block .in {
              width: 100%; height: 48px; padding: 0 14px; background: #fff;
              border: 1px solid rgba(15,24,41,0.25); border-radius: 6px;
              color: #0F1829; font-size: 14px; font-family: inherit;
              line-height: 1.2; box-sizing: border-box;
              transition: border-color 0.2s;
            }
            .ihtp-form-block select.in { padding: 0 40px 0 14px; }
            .ihtp-form-block .in::placeholder { color: rgba(15,24,41,0.4); }
            .ihtp-form-block .in:focus { outline: none; border-color: #0F1829; }
            .ihtp-form-block .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
            @media (max-width: 640px) { .ihtp-form-block .grid2 { grid-template-columns: 1fr; } }
            .ihtp-form-block .pw-wrap { position: relative; }
            .ihtp-form-block .pw-wrap button {
              position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
              background: transparent; border: 0; color: rgba(15,24,41,0.55); cursor: pointer;
              display: flex; align-items: center;
            }
            .ihtp-form-block .divider {
              display: flex; align-items: center; gap: 12px; margin: 24px 0 16px;
            }
            .ihtp-form-block .divider .line { flex: 1; height: 1px; background: rgba(15,24,41,0.15); }
            .ihtp-form-block .divider .txt {
              font-size: 10px; letter-spacing: 0.3em; text-transform: uppercase;
              color: rgba(15,24,41,0.5); font-weight: 600; white-space: nowrap;
            }
            .ihtp-form-block .sexo { display: flex; gap: 8px; }
            .ihtp-form-block .sexo label {
              flex: 1; height: 48px; display: flex; align-items: center; justify-content: center; gap: 8px;
              padding: 0 12px; border: 1px solid rgba(15,24,41,0.25); border-radius: 6px;
              font-size: 14px; cursor: pointer; transition: all 0.2s; color: #0F1829; background: #fff;
              box-sizing: border-box;
            }
            .ihtp-form-block .sexo label.on { border-color: #0F1829; background: rgba(15,24,41,0.06); }
            .ihtp-form-block .banner {
              padding: 12px 14px; margin-bottom: 20px; border-radius: 6px; font-size: 13px;
              background: rgba(15,24,41,0.06); border: 1px solid rgba(15,24,41,0.2);
              color: #0F1829; text-align: center;
            }
            .ihtp-form-block .banner.err {
              background: #FEE2E2; border: 2px solid #DC2626; color: #991B1B;
              font-size: 14px; font-weight: 700; text-align: left;
              display: flex; align-items: flex-start; gap: 8px;
              box-shadow: 0 6px 18px rgba(220,38,38,0.18);
              animation: ihtpErr 0.35s ease;
            }
            .ihtp-form-block .banner.err .ic {
              flex: 0 0 auto; width: 18px; height: 18px; border-radius: 50%;
              background: #DC2626; color: #fff; font-size: 12px; line-height: 18px;
              text-align: center; font-weight: 900;
            }
            @keyframes ihtpErr {
              0% { transform: translateX(0); } 25% { transform: translateX(-6px); }
              50% { transform: translateX(6px); } 75% { transform: translateX(-3px); }
              100% { transform: translateX(0); }
            }
            .ihtp-form-block .in.bad, .ihtp-form-block .sexo.bad label {
              border-color: #DC2626 !important; background: #FEF2F2;
            }
            .ihtp-form-block .terms {
              display: flex; align-items: flex-start; gap: 10px; margin: 20px 0;
              font-size: 13px; color: #0F1829; line-height: 1.6;
            }
            .ihtp-form-block .terms a { color: #0F1829; text-decoration: underline; }
            .ihtp-form-block .btn {
              display: inline-flex; align-items: center; justify-content: center; gap: 8px;
              min-width: 260px; padding: 14px 32px; border-radius: 99px; border: 1px solid #0F1829;
              background: #0F1829; color: #fff; font-size: 12px; font-weight: 600;
              letter-spacing: 0.15em; text-transform: uppercase; cursor: pointer;
              transition: all 0.2s; font-family: inherit;
            }
            .ihtp-form-block .btn:hover:not(:disabled) { background: transparent; color: #0F1829; }
            .ihtp-form-block .btn:disabled { opacity: 0.5; cursor: not-allowed; }
            .ihtp-form-block .foot { text-align: center; margin-top: 20px; font-size: 13px; color: rgba(15,24,41,0.65); }
            .ihtp-form-block .foot a { color: #0F1829; text-decoration: underline; }
          `}</style>

          {referId ? (
            <div data-testid="register-refer-banner" className="banner">
              Convite por <span className="notranslate" style={{ fontWeight: 700 }}>#{referId}</span> activo. Serás associado a este consultor.
            </div>
          ) : null}
          {error ? (
            <div data-testid="register-error" className="banner err" role="alert" aria-live="assertive">
              <span className="ic">!</span><span>{error}</span>
            </div>
          ) : null}

          <SocialAuthButtons />
          <SocialAuthDivider />

          <form onSubmit={submit} noValidate>
            <div className="fld">
              <label className="lbl">Nome*</label>
              <input id="reg-nome_completo" data-testid="register-nome" type="text" value={form.nome_completo}
                onChange={(e) => upd("nome_completo", e.target.value)} required
                className={`in${errorField === "nome_completo" ? " bad" : ""}`} placeholder="Nome completo" />
            </div>
            <div className="grid2">
              <div className="fld">
                <label className="lbl">Email*</label>
                <input id="reg-email" data-testid="register-email" type="email" value={form.email}
                  onChange={(e) => { upd("email", e.target.value); emailChk.reset(); }}
                  onBlur={(e) => emailChk.check(e.target.value)}
                  required className={`in${errorField === "email" ? " bad" : ""}`} placeholder="email@exemplo.com" />
                <EmailHint state={emailChk} testid="register-email-hint"
                  onUseSuggestion={(v) => { upd("email", v); emailChk.reset(); }} />
              </div>
              <div className="fld">
                <label className="lbl">Número de telemóvel*</label>
                <input id="reg-telemovel" data-testid="register-telemovel" type="tel" value={form.telemovel}
                  onChange={(e) => upd("telemovel", e.target.value)} required
                  className={`in${errorField === "telemovel" ? " bad" : ""}`} placeholder="PT (+351) 912 345 678" />
              </div>
            </div>
            <div className="grid2">
              <div className="fld">
                <label className="lbl">Password*</label>
                <div className="pw-wrap">
                  <input id="reg-password" data-testid="register-password" type={showPw ? "text" : "password"}
                    value={form.password} onChange={(e) => upd("password", e.target.value)}
                    required minLength={8} className="in" placeholder="Escolher (min. 8)" style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowPw(!showPw)} aria-label="Mostrar palavra-passe">
                    {showPw ? <Eye size={18} /> : <EyeSlash size={18} />}
                  </button>
                </div>
              </div>
              <div className="fld">
                <label className="lbl">Confirmar password*</label>
                <div className="pw-wrap">
                  <input id="reg-confirm_password" data-testid="register-password2" type={showPw2 ? "text" : "password"}
                    value={form.confirm_password} onChange={(e) => upd("confirm_password", e.target.value)}
                    required minLength={8} className="in" placeholder="Repetir" style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowPw2(!showPw2)} aria-label="Mostrar confirmação">
                    {showPw2 ? <Eye size={18} /> : <EyeSlash size={18} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="grid2">
              <div className="fld">
                <label className="lbl">Data de nascimento*</label>
                <input id="reg-data_nascimento" data-testid="register-data-nasc" type="text" value={form.data_nascimento}
                  onChange={(e) => upd("data_nascimento", e.target.value)}
                  required placeholder="DD-MM-AAAA" pattern="\d{2}-\d{2}-\d{4}"
                  max={today()} className={`in${errorField === "data_nascimento" ? " bad" : ""}`} />
              </div>
              <div className="fld">
                <label className="lbl">Sexo*</label>
                <div id="reg-sexo" className={`sexo${errorField === "sexo" ? " bad" : ""}`}>
                  {[["M", "Masculino"], ["F", "Feminino"]].map(([v, lbl]) => (
                    <label key={v} className={form.sexo === v ? "on" : ""}>
                      <input data-testid={`register-sexo-${v}`} type="radio" name="sexo" value={v}
                        checked={form.sexo === v} onChange={() => upd("sexo", v)} style={{ accentColor: "#fff" }} />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="divider">
              <div className="line" />
              <div className="txt">Campos opcionais</div>
              <div className="line" />
            </div>

            <div className="grid2">
              <div className="fld">
                <label className="lbl">País</label>
                <select data-testid="register-pais" value={form.pais}
                  onChange={(e) => upd("pais", e.target.value)} className="in"
                  style={{
                    appearance: "none",
                    backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='%23ffffff' opacity='0.6' d='M6 8L1 3h10z'/></svg>\")",
                    backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
                  }}>
                  <option value="" style={{ color: "#000" }}>Escolher</option>
                  {PAISES.map((p) => <option key={p} value={p} style={{ color: "#000" }}>{p}</option>)}
                </select>
              </div>
              <div className="fld">
                <label className="lbl">Código Postal</label>
                <input data-testid="register-codigo-postal" type="text" value={form.codigo_postal}
                  onChange={(e) => upd("codigo_postal", e.target.value)}
                  placeholder="XXXX-XXX" className="in" />
              </div>
            </div>
            <div className="fld">
              <label className="lbl">Localidade</label>
              <input data-testid="register-localidade" type="text" value={form.localidade}
                onChange={(e) => upd("localidade", e.target.value)} className="in" placeholder="Cidade" />
            </div>
            <div className="fld">
              <label className="lbl">
                Email do recomendador <span style={{ opacity: 0.5, textTransform: "none", letterSpacing: "normal", fontWeight: 400 }}>(opcional — sobrepõe o convite)</span>
              </label>
              <input data-testid="register-email-recomendacao" type="email"
                value={form.email_recomendacao}
                onChange={(e) => upd("email_recomendacao", e.target.value)}
                placeholder="email@exemplo.com" className="in" />
            </div>

            <div className="terms">
              <input data-testid="register-terms" type="checkbox" checked={form.aceito_termos}
                onChange={(e) => upd("aceito_termos", e.target.checked)} required
                style={{ marginTop: 3, accentColor: "#fff" }} />
              <span>
                Li e aceito os <a href="https://ihavethepower.net/protecao-de-dados-e-utilizacao/" target="_blank" rel="noopener noreferrer">Termos e Condições</a>.
              </span>
            </div>

            <div style={{ textAlign: "center", marginTop: 20 }}>
              {error ? (
                <div data-testid="register-error-inline" className="banner err" role="alert">
                  <span className="ic">!</span><span>{error}</span>
                </div>
              ) : null}
              <button type="submit" disabled={loading} data-testid="register-submit" className="btn">
                {loading ? <SpinnerGap size={16} className="animate-spin" /> : null}
                {loading ? "A criar conta…" : "Registar-me gratuitamente"}
              </button>
              <p className="foot">Já tem conta? <Link to="/login">Entrar</Link></p>
              <p className="foot" style={{ marginTop: 8 }}>
                <a href="https://www.ihavethepower.net" data-testid="register-go-to-site"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  Ir para site
                  <ArrowSquareOut size={14} />
                </a>
              </p>
            </div>
          </form>
        </div>,
        mount
      )}
      <SiteFooter />
    </>
  );
}
